import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  List,
  Stack,
  Typography,
} from "@mui/material";
import type {
  SubscriptionResponse,
  VisitBalanceResponse,
} from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { useMyMemberships } from "../hooks/useMyMemberships";
import { useVisitBalances } from "../hooks/useVisitBalances";
import { MembershipRow } from "../components/MembershipRow";

/**
 * My Memberships (`/me/memberships`) — the management home, mirroring
 * /me/bookings. Lists the caller's subscriptions (server-scoped, enriched) with
 * their honest lifecycle state, joins each visits_card pass to its balance, and
 * groups by facility when there are memberships across several venues. A row
 * links into the membership detail (cancel / reactivate / billing history).
 *
 * Empty state points at membership discovery (/play?activity=memberships) so a member with no
 * subscriptions can find a facility's plans — no dead end.
 */
export function MyMembershipsPage() {
  const subsQ = useMyMemberships();
  const balancesQ = useVisitBalances();

  // Index balances by subscription_id for an O(1) join in the row.
  const balanceBySub = useMemo(() => {
    const map = new Map<string, VisitBalanceResponse>();
    for (const b of balancesQ.data ?? []) map.set(b.subscription_id, b);
    return map;
  }, [balancesQ.data]);

  // Group subscriptions by facility (server order preserved within a group).
  // facility_name is enriched; a null degrades to an honest fallback label —
  // never the raw facility_id.
  const groups = useMemo(() => {
    const out: { label: string; subs: SubscriptionResponse[] }[] = [];
    const byLabel = new Map<string, SubscriptionResponse[]>();
    for (const s of subsQ.data ?? []) {
      const label = s.facility_name?.trim() || "Your facility";
      let bucket = byLabel.get(label);
      if (!bucket) {
        bucket = [];
        byLabel.set(label, bucket);
        out.push({ label, subs: bucket });
      }
      bucket.push(s);
    }
    return out;
  }, [subsQ.data]);

  if (subsQ.isLoading) {
    return (
      <Page title="My memberships">
        <Stack
          direction="row"
          spacing={1.5}
          role="status"
          sx={{ alignItems: "center" }}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary">
            Loading your memberships…
          </Typography>
        </Stack>
      </Page>
    );
  }

  if (subsQ.isError) {
    return (
      <Page title="My memberships">
        <Alert severity="error">
          We couldn&apos;t load your memberships. Please try again.
        </Alert>
      </Page>
    );
  }

  const subs = subsQ.data ?? [];
  if (subs.length === 0) {
    return (
      <Page title="My memberships">
        <Stack spacing={1.5} role="status" sx={{ alignItems: "flex-start" }}>
          <Typography color="text.secondary">
            You don&apos;t have any memberships yet.
          </Typography>
          <Button
            component={RouterLink}
            to="/play?activity=memberships"
            variant="contained"
            size="small"
          >
            Find a facility
          </Button>
        </Stack>
      </Page>
    );
  }

  // A single facility → skip the group header (a lone label is noise). Multiple
  // facilities → label each group.
  const showGroupLabels = groups.length > 1;

  return (
    <Page title="My memberships">
      <Stack spacing={3}>
        {groups.map((group) => (
          <Stack component="section" key={group.label} spacing={1.25}>
            {showGroupLabels ? (
              <Typography component="h2" variant="h6">
                {group.label}
              </Typography>
            ) : null}
            <List
              disablePadding
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {group.subs.map((sub) => (
                <MembershipRow
                  key={sub.subscription_id}
                  subscription={sub}
                  balance={balanceBySub.get(sub.subscription_id)}
                />
              ))}
            </List>
          </Stack>
        ))}
      </Stack>
    </Page>
  );
}
